import {ReactElement, useMemo} from "react";
import {
    AcctRenovationPackagesQuery,
    GetAcctRenoMoveOutRatesQuery,
    UpdateRenovationPackageCountMutationOptions,
    VersionType,
} from "../../../../__generated__/generated_types";
import { Property } from '../../../../contexts/properties/PropertiesContext';
import {IVZHotTableRow, VZHotTable} from "../../../../components/vz-ui/vz-hot-table/VZHotTable";
import {getMonths} from "../../../../utils/months-helper";
import {VZHotTableConfig} from "../../../../components/vz-ui/vz-hot-table/vz-hot-table-config";
import {toast} from "react-toastify";
import {Button} from "@zendeskgarden/react-buttons";
import { BiWrench } from 'react-icons/bi';


export interface AcctRenovationsSSProps {
    renovationData: AcctRenovationPackagesQuery | undefined;
    moveOutRatesData: GetAcctRenoMoveOutRatesQuery | undefined;
    dataLoading: boolean;
    currentVersionYear: number;
    property: Property | undefined;
    unitTypeId: string;
    versionType: VersionType;
    readOnly: boolean;
    userIsAdmin: boolean;
    budgetYear: number;
    onOpenDrawer: () => void;
    onSaveChanges: (payloads: UpdateRenovationPackageCountMutationOptions[]) => void;
}

export default function AcctRenovationsTable(props: AcctRenovationsSSProps): ReactElement {
    const {
        renovationData,
        moveOutRatesData,
        dataLoading,
        property,
        versionType,
        unitTypeId,
        currentVersionYear,
        readOnly = false,
        userIsAdmin = false,
        onOpenDrawer,
        onSaveChanges,
    } = props;

    const {tableData, tableConfig} = useMemo<{tableData: IVZHotTableRow[], tableConfig: VZHotTableConfig}>(() => {
        const tableData: IVZHotTableRow[] = [];
        const tableConfig = new VZHotTableConfig();

        if (!property || !renovationData || !moveOutRatesData || dataLoading) {
            return {tableData, tableConfig};
        }

        const packagesForUnitType = renovationData?.queryRenovationPackages.groupBy("unitTypeId")[unitTypeId]?.sortBy("createdAt") ?? [];
        const expiringMoveOutsActuals = moveOutRatesData.actuals?.[0]?.unitTypeScheduleValues.sortBy("monthIndex");
        const expiringMoveOutsReforecast = moveOutRatesData.reforecast?.[0]?.unitTypeScheduleValues.sortBy("monthIndex");
        const expiringMoveOutsBudget = moveOutRatesData.budget?.[0]?.unitTypeScheduleValues.sortBy("monthIndex");

        let expiringMoveOuts = new Array(12).fill(0);

        if (versionType == VersionType.Reforecast && expiringMoveOutsReforecast) {
            expiringMoveOuts = expiringMoveOutsReforecast.map((rfcstValues, monthIndex) => {
                const actualsValues = expiringMoveOutsActuals?.[monthIndex];
                let expirations = rfcstValues.leaseExpirationCount;
                let moveOutRate = rfcstValues.moveOutRate;

                if (monthIndex < property.reforecastStartMonthIndex) {
                    expirations = actualsValues?.leaseExpirationCount ?? 0;
                    moveOutRate = actualsValues?.moveOutRate ?? 0;
                }

                return (expirations * moveOutRate).toFixed(1);
            });
        } else if (versionType == VersionType.Budget && expiringMoveOutsBudget) {
            expiringMoveOuts = expiringMoveOutsBudget.map(x => (x.leaseExpirationCount * x.moveOutRate).toFixed(1));
        }

        for (const renoPackage of packagesForUnitType) {
            const renovationNumbersForVersionYear = renoPackage.renovationNumbers.find(x => x.versionType == versionType && x.year == currentVersionYear);
            let renovationCounts = new Array(12).fill(0);

            if (renovationNumbersForVersionYear) {
                renovationCounts = renovationNumbersForVersionYear.expected.sortBy("monthIndex").map(x => x.value ?? 0);
            }

            tableData.push({
                label: renoPackage.name,
                rowId: renoPackage.id,
                rowType: "child",
                parentRowId: "total-renovations",
                values: renovationCounts,
            });
        }

        tableData.push({
            label: "Total Renovations",
            rowType: "summary",
            rowId: "total-renovations",
            values: new Array(12).fill(0),
        });

        tableData.push({
            label: "Total Expiring Move Outs",
            values: expiringMoveOuts,
        });

        const tableLocked = readOnly;
        let writeableColumnsStart = 1;

        // We do not have integration for loading in actual renovation numbers into the packages
        // so just let users enter the numbers in the UI. Anyways the backend does not make distinction
        // between reforecast and actuals and considers them all as reforecast.
        // if (versionType == VersionType.Reforecast) {
        //     writeableColumnsStart = property.reforecastStartMonthIndex + 1;
        // }

        tableConfig.tableSummaryFunction = "sum";
        tableConfig.applyToColumnAndFollowing(writeableColumnsStart, {isReadOnly: tableLocked, format: "count"});
        tableConfig.applyToColumnAndFollowing(13, {isReadOnly: true, isGreyBackground: true, isBoldText: true});
        tableConfig.applyToRowAndFollowing(tableData.length - 2, {format: "count", isReadOnly: true, isGreyBackground: true, isBoldText: true});
        tableConfig.applyToColumn(0, {isRowHeader: true, isBoldText: true, format: "string"});

        if (versionType == VersionType.Reforecast) {
            tableConfig.applyToColumn(writeableColumnsStart, {isThickBorderLeft: true});
        }

        return {tableData, tableConfig};
    }, [property, currentVersionYear, versionType, renovationData, moveOutRatesData, dataLoading]);

    return (
        <VZHotTable
            data={tableData}
            colWidths={[140]}
            colHeaders={["Renovation Packages", ...getMonths(property), "Total"]}
            tableConfig={tableConfig}
            isDataLoading={dataLoading}
            isSearchable
            isUndoRedoable
            onChange={(changes) => {
                const payloads: UpdateRenovationPackageCountMutationOptions[] = [];

                for (const [row, monthIndex, value] of changes) {
                    const packageId = tableData[row]?.rowId;

                    if (!packageId) {
                        toast.error("Issue with package ID, please refresh and try again");
                        return;
                    }

                    payloads.push({
                        variables:{
                            renovationPackageId: packageId,
                            yearAndType: {
                                versionType,
                                year: currentVersionYear,
                            },
                            expected: {
                                monthIndex,
                                value,
                            },
                            budgetYear: props.budgetYear
                        }
                    });
                }

                onSaveChanges(payloads);
            }}
            customControls={
                <>
                    {userIsAdmin &&
                        <Button isPrimary onClick={onOpenDrawer}>
                            <Button.StartIcon><BiWrench /></Button.StartIcon>
                            Setup Packages
                        </Button>
                    }
                </>
            }
        />
    );
}
